// @ts-strict-ignore
import { MultiSelect } from '@usealloy/component-library';
import {
  forwardRef,
  useCallback,
  useContext,
  useMemo,
} from 'react';

import { MULTISELECT_PROPS } from 'flow/editor/components/constants';
import { useEditorAccess } from 'flow/editor/context';
import type { Action } from 'flow/store/engine/types';
import { getActionName, getNamesOfOutcomesInWorkflow } from 'flow/service/actions';
import { getIsEditable } from 'flow/service/general';
import {
  createWorkflowHistoryThunk,
  replaceDefaultOutcomeThunk,
  swapActionObjectThunk,
} from 'flow/store/redux/thunks';
import {
  selectOutcomeActions, selectWorkflowProps,
} from 'flow/store/redux/selectors';

import { UserContext } from '~/dashboard/context';
import { getActiveOutcomes, getOutcomeByName } from '~/dashboard/views/workflows/service/outcomes/outcomes';
import { Operation } from 'flow/service/changelog/types';
import { createUiEffectContext } from 'flow/service/history';
import { changeDescription } from 'flow/store/redux/types';
import { useAppDispatch, useAppSelector } from '~/store/reduxHooks';

import { buildOutcomeOption, buildOutcomeOptions } from './helpers';
import type { OutcomeOption } from './types';
import SingleValue from './SingleValue';


interface OutcomeDropdownProps {
  isDefaultAction?: boolean;
  action: Action;
  dataTestId?: string;
}

const OutcomeDropdown = forwardRef<any, OutcomeDropdownProps>(({
  isDefaultAction,
  action,
  dataTestId,
}, ref) => {
  const dispatch = useAppDispatch();
  const { featureFlags } = useContext(UserContext);
  const { outcomes } = useAppSelector(selectWorkflowProps);
  const outcomeActions = useAppSelector(selectOutcomeActions);
  const isWorkflowHistoryOn = Boolean(featureFlags?.workflowEditorUndoRedo);

  const { editorMode } = useEditorAccess();
  const inEditMode = getIsEditable(editorMode);

  const namesOfOutcomesInWorkflow = useMemo(
    () => getNamesOfOutcomesInWorkflow(outcomeActions),
    [outcomeActions],
  );
  const outcomeOptions = useMemo(() => buildOutcomeOptions({
    outcomes: getActiveOutcomes(outcomes),
    namesOfOutcomesInWorkflow,
  }), [namesOfOutcomesInWorkflow, outcomes]);
  // The selected outcome may have been archived since it was saved to this workflow.
  // So, we find & build it from the set of all outcomes - which includes archived outcomes.
  const initialSelectedOutcome = getOutcomeByName(outcomes, getActionName(action));

  const selectedOutcomeOption = initialSelectedOutcome
    ? buildOutcomeOption({
      outcome: initialSelectedOutcome,
      namesOfOutcomesInWorkflow,
    })
    : null;

  const handleOnChange = useCallback((option: OutcomeOption) => {
    const replaceOutcome = () => {
      if (isDefaultAction) {
        dispatch(replaceDefaultOutcomeThunk(option.value));
      } else {
        dispatch(swapActionObjectThunk(option.value));
      }
    };

    dispatch(createWorkflowHistoryThunk(
      replaceOutcome,
      isWorkflowHistoryOn,
      {
        operation: Operation.Modify,
        uiEffectContext: createUiEffectContext(changeDescription.action, {
          publicId: action.publicId,
        }),
      },
    ));
  }, [action.publicId, dispatch, isDefaultAction, isWorkflowHistoryOn]);

  return (
    <span
      data-testid={dataTestId}
      data-value={selectedOutcomeOption?.['data-value'] || ''}
    >
      <MultiSelect
        // todo BUILD-637 - refactor MULTISELECT_PROPS to make it easier to compose new styles
        {...MULTISELECT_PROPS}
        ref={ref}
        isDisabled={!inEditMode}
        openMenuOnFocus
        customStyleFunctions={{
          ...MULTISELECT_PROPS.customStyleFunctions,
          container: styles => ({
            ...styles,
            margin: '0',
            minWidth: '80',
          }),
        }}
        options={outcomeOptions}
        onChange={handleOnChange}
        selectedOptions={[selectedOutcomeOption]}
        // FIXME: BUILD-1368
        isOptionDisabled={(o: OutcomeOption) => o.disabled}
        components={{
          SingleValue,
        }}
      />
    </span>
  );
});

OutcomeDropdown.displayName = 'OutcomeDropdown';
export default OutcomeDropdown;
